<template>
  <fieldset
    :id="id"
    class="rate-quote-accessorial-edit flex flex-gap-1"
    tabindex="-1"
  >
    <legend class="visually-hidden">
      {{ t('referenceData.accessorials') }}
    </legend>

    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :input-id="`${id}-accessorial`"
      class="w-4"
      :label="t('referenceData.accessorials')"
      :errors="v$.code.$errors"
    >
      <RygenDropdown
        :id="inputId"
        v-model="v$.code.$model"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :options="availableAccessorialsWithExclusions"
        :placeholder="t('referenceData.accessorials')"
        append-to="body"
        option-label="name"
        data-key="naturalId"
        filter
      />
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputId }"
      :input-id="`${id}-stop`"
      class="w-4"
      :label="t('shipments.stops')"
      optional
    >
      <RygenDropdown
        :id="inputId"
        v-model="v$.stopSequenceNumber.$model"
        v-bind="ariaProps"
        :options="stops"
        :placeholder="t('shipments.stops')"
        append-to="body"
        option-label="location.locationName"
        option-value="sequenceNumber"
        filter
        show-clear
      >
        <template #option="{ option }">
          <StopLabel :stop="option" />
        </template>

        <template v-if="!!selectedStop" #value>
          <StopLabel :stop="selectedStop" />
        </template>
      </RygenDropdown>
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :input-id="`${id}-accessorial-charge`"
      class="w-4"
      :label="t('common.charge')"
      :errors="v$.charge.$errors"
    >
      <div class="flex flex-gap-1 align-items-center">
        <InputNumber
          v-model="v$.charge.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :input-id="inputId"
          :placeholder="t('common.charge')"
          class="flex-grow-1"
          currency="USD"
          mode="currency"
          use-grouping
        />

        <InlineRemoveButton
          :aria-controls="id"
          @click="emit('remove', internalValue)"
        />
      </div>
    </InputWrapper>
  </fieldset>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, toRef, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  minValue,
  required,
  useModelValue,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  AccessorialCode,
  AccessorialCodeWellKnownNaturalId,
} from '@rygen/rygen-ui/model';

import { RateQuoteAccessorial } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import StopLabel from '@/components/location/StopLabel.vue';
import InlineRemoveButton from '@/components/ui/InlineRemoveButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import InputNumber from 'primevue/inputnumber';

const ACCESSORIAL_CODES_TO_EXCLUDE: Set<string> = new Set([
  AccessorialCodeWellKnownNaturalId.LINEHAUL,
  AccessorialCodeWellKnownNaturalId.FUEL,
  AccessorialCodeWellKnownNaturalId.USER_LICENSE,
  AccessorialCodeWellKnownNaturalId.PASSTHROUGH,
  AccessorialCodeWellKnownNaturalId.FUM,
]);

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: RateQuoteAccessorial,
    default: () => new RateQuoteAccessorial(),
  },
  availableAccessorials: {
    type: Array as PropType<AccessorialCode[]>,
    default: () => [],
  },
});
const { id, modelValue, availableAccessorials } = toRefs(props);
const emit = defineEmits<{
  (event: 'remove', rateQuoteAccessorial: RateQuoteAccessorial): void;
  (event: 'update:model-value', newVal: RateQuoteAccessorial): void;
}>();

const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<RateQuoteAccessorial>) =>
    new RateQuoteAccessorial(opts),
});
const { parentEntity } = useParentEntityStore();

const rules = {
  code: {
    required: required(t('referenceData.accessorials')),
  },
  charge: {
    minValue: minValue(0.01, t('common.charge')),
    required: required(t('common.charge')),
  },
  stopSequenceNumber: {},
};
const { v$ } = useValidations(internalValue, rules);

const stops = toRef(() => parentEntity.value?.stops ?? []);

const selectedStop = computed(() =>
  stops.value.find(
    (s) => s.sequenceNumber === internalValue.value.stopSequenceNumber,
  ),
);

const availableAccessorialsWithExclusions: Ref<AccessorialCode[]> = computed(
  () => {
    return availableAccessorials.value.filter(
      (accessorial) =>
        !ACCESSORIAL_CODES_TO_EXCLUDE.has(accessorial.naturalId ?? ''),
    );
  },
);
</script>

<style>
.rate-quote-accessorial-edit {
  margin: 0;
  padding: 0;
  border: 0;

  .w-4 {
    /* prevent the charge input from overflowing its container */
    .p-inputtext.p-component {
      max-width: 100%;
    }
  }
}
</style>
